// Helpers de apresentação de deals (Story 11.4 + 11.5 kanban).
// Centraliza o cálculo de "dias no estágio" (AC10) e a formatação de valor,
// compartilhados entre a lista, o detalhe e o kanban de deals. Disciplina de
// timezone: usa helpers de @control-center/shared (nada de new Date("YYYY-MM-DD")
// / toLocale pra datas — formatDealMoney usa toLocaleString só pra moeda, que é
// permitido pela regra de timezone).

import type { CrmDealStageSchema } from '@control-center/shared';
import { daysDiffLocal, toYMDLocal, todayLocal } from '@control-center/shared';

/**
 * Dias no estágio atual — diferença entre hoje e stage_entered_at no fuso BR.
 * Nunca negativo (clamp em 0 — defesa contra clock skew / data futura).
 */
export function daysInStage(stageEnteredAt: string, today: string = todayLocal()): number {
  const diff = daysDiffLocal(toYMDLocal(stageEnteredAt), today);
  return diff < 0 ? 0 : diff;
}

/** Formata valor monetário (string Decimal do backend) em BRL, ou '—' se null. */
export function formatDealMoney(value: string | null): string {
  if (value === null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Soma valores (string Decimal) de uma lista, formatada em BRL. */
export function sumDealMoney(values: (string | null)[]): string {
  const total = values.reduce((acc, v) => acc + (v ? Number(v) : 0), 0);
  return total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// -----------------------------------------------------------------------------
// Kanban (Story 11.5) — ordem fixa de estágios (DR-039) + rótulos.
// -----------------------------------------------------------------------------

/** Rótulo PT-BR de cada estágio do funil. Fonte única (lista + detalhe + kanban). */
export const STAGE_LABELS: Record<CrmDealStageSchema, string> = {
  novo_lead: 'Novo lead',
  qualificacao: 'Qualificação',
  diagnostico: 'Diagnóstico',
  proposta_enviada: 'Proposta enviada',
  negociacao: 'Negociação',
  fechamento_ganho: 'Fechamento ganho',
  fechamento_perdido: 'Fechamento perdido',
};

/**
 * Estágios abertos do funil (colunas operacionais do kanban) — ordem fixa DR-039.
 * Deals nesses estágios estão `status=open` e são a origem/destino do drag.
 */
export const OPEN_STAGES: CrmDealStageSchema[] = [
  'novo_lead',
  'qualificacao',
  'diagnostico',
  'proposta_enviada',
  'negociacao',
];

/** Estágios terminais — só destino de drop (fecham o deal won/lost). */
export const TERMINAL_STAGES: CrmDealStageSchema[] = ['fechamento_ganho', 'fechamento_perdido'];

/** Estágio terminal de perda — exige motivo ao mover (AC6). */
export const LOST_STAGE: CrmDealStageSchema = 'fechamento_perdido';

/** Mensagem de erro humana por código ao mover estágio (AC5). */
export function moveStageErrorMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'VERSION_CONFLICT':
      return 'Outra pessoa moveu este deal ao mesmo tempo. Atualize a página.';
    case 'DEAL_CLOSED':
      return 'Este deal já está fechado e não pode ser movido.';
    default:
      return fallback;
  }
}
